/* Qik Joe — the recommender

   Three questions, then a shortlist. Scoring is deliberately transparent: the
   result tells the visitor *why* each product came back, because "we recommend
   this" with no reasoning is how people end up buying the wrong bag and
   blaming the brand.

   Product attributes come from the section's blocks, which the owners fill in.
   Nothing here infers whether a product is pet-safe — that is a claim about a
   chemical, and it has to come from the plant, not from a script.

   Uses <dialog> for the modal: focus trapping, Escape, and inertness of the
   page behind it are all handled by the browser. */

(function () {
  'use strict';

  var SEEN_KEY = 'qj:quiz-seen';

  // The answer values are machine-readable; these are how they get spoken back.
  var NO_MELT_AT_TEMP =
    'No ice melt in the range is both rated that low and marked safe around ' +
    'pets. The traction aid works at any temperature; the melts below are the ' +
    'closest, with their ratings shown.';
  var NO_MATCH_TEMP =
    'Nothing in the range is rated that low. These are the closest, and they ' +
    'will still work above their rating.';
  var NO_MATCH_PETS =
    'Nothing here is marked safe around pets. These melt well, but keep animals ' +
    'off the surface until it has been rinsed — or call the plant on ' +
    '(570) 654-2433.';

  var SURFACE_LABEL = {
    'driveway': 'a driveway',
    'walks-steps': 'walks and steps',
    'lot': 'a lot'
  };

  function Quiz(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-quiz-dialog]');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]'));
    this.resultStep = root.querySelector('[data-quiz-result]');
    this.resultList = root.querySelector('[data-quiz-list]');
    this.emptyMsg = root.querySelector('[data-quiz-empty]');
    this.caveatMsg = root.querySelector('[data-quiz-caveat]');
    this.progress = root.querySelector('[data-quiz-progress]');
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-item]'));

    this.answers = {};
    this.index = 0;

    var self = this;

    Array.prototype.forEach.call(document.querySelectorAll('[data-quiz-open]'), function (btn) {
      btn.addEventListener('click', function () { self.open(); });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-choice]'), function (btn) {
      btn.addEventListener('click', function () {
        var step = btn.closest('[data-quiz-step]');
        self.answers[step.getAttribute('data-quiz-step')] = btn.getAttribute('data-quiz-choice');
        self.next();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-close]'), function (btn) {
      btn.addEventListener('click', function () { self.dialog.close(); });
    });

    var restart = root.querySelector('[data-quiz-restart]');
    if (restart) restart.addEventListener('click', function () { self.reset(); });

    // Optional delayed auto-open. Off by default: an unprompted modal is the
    // fastest way to make a visitor leave. When it is switched on it fires
    // once per browser, not once per page view.
    var delay = parseInt(root.getAttribute('data-quiz-autoopen'), 10);
    if (delay > 0 && !this.seen()) {
      window.setTimeout(function () {
        if (!self.dialog.open) { self.open(); self.markSeen(); }
      }, delay * 1000);
    }
  }

  Quiz.prototype.seen = function () {
    try { return window.localStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; }
  };

  Quiz.prototype.markSeen = function () {
    try { window.localStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* storage off */ }
  };

  Quiz.prototype.open = function () {
    this.reset();
    this.dialog.showModal();
  };

  Quiz.prototype.reset = function () {
    this.answers = {};
    this.index = 0;
    this.show();
  };

  Quiz.prototype.next = function () {
    this.index++;
    if (this.index >= this.steps.length) this.score();
    this.show();
  };

  Quiz.prototype.show = function () {
    var done = this.index >= this.steps.length;

    for (var i = 0; i < this.steps.length; i++) {
      this.steps[i].hidden = done || i !== this.index;
    }
    if (this.resultStep) this.resultStep.hidden = !done;

    if (this.progress) {
      this.progress.textContent = done
        ? 'Result'
        : (this.index + 1) + ' of ' + this.steps.length;
    }

    var focusable = (done ? this.resultStep : this.steps[this.index]);
    if (focusable) {
      var target = focusable.querySelector('button, a, [tabindex]');
      if (target) target.focus();
    }
  };

  Quiz.prototype.score = function () {
    var surface = this.answers.surface;
    var cold = parseInt(this.answers.cold, 10);
    var pets = this.answers.pets === 'yes';

    var best = [];      // meets everything they asked for
    var near = [];      // right on safety, short on temperature
    var petless = [];   // nothing else left, and they have pets

    for (var i = 0; i < this.items.length; i++) {
      var el = this.items[i];
      var traction = el.hasAttribute('data-quiz-traction');
      var low = parseInt(el.getAttribute('data-quiz-low'), 10);
      var petSafe = el.hasAttribute('data-quiz-pet');

      // Surface is a preference, so it scores rather than filters.
      var points = 0;
      var surfaceReason = null;
      var surfaces = (el.getAttribute('data-quiz-surfaces') || '').split(',');
      for (var t = 0; t < surfaces.length; t++) {
        if (surfaces[t].trim() === surface) {
          points += 2;
          surfaceReason = 'suited to ' + SURFACE_LABEL[surface];
          break;
        }
      }

      var entry = { el: el, points: points, reasons: [], caveats: [] };
      if (surfaceReason) entry.reasons.push(surfaceReason);
      if (traction) entry.reasons.push('traction only, no melting');

      // Temperature. A traction aid has no melt rating to fail against.
      var warmEnough = traction || isNaN(low) || isNaN(cold) || low <= cold;
      if (!traction && !isNaN(low)) {
        if (warmEnough) {
          entry.reasons.push('rated to ' + low + '°F');
          entry.points += 3;
        } else {
          entry.caveats.push('only rated to ' + low + '°F');
          entry.gap = low - cold;
        }
      }

      // Pet safety stays a hard line. Recommending an unmarked product to
      // somebody who told us they have animals is not a ranking decision.
      if (pets) {
        if (!petSafe) { petless.push(entry); continue; }
        entry.reasons.push('marked safe around pets');
        entry.points += 3;
      }

      (warmEnough ? best : near).push(entry);
    }

    var list = best;
    var caveat = '';

    // A result made up only of traction aids is technically correct and
    // practically useless: somebody asking for pet-safe at 0F is owed the
    // nearest pet-safe *melt* as well, with its shortfall stated. Otherwise the
    // honest answer looks like a broken one.
    var hasMelt = false;
    for (var m = 0; m < best.length; m++) {
      if (!best[m].el.hasAttribute('data-quiz-traction')) { hasMelt = true; break; }
    }
    if (best.length && !hasMelt && near.length) {
      near.sort(function (a, b) { return a.gap - b.gap; });
      list = best.concat(near);
      caveat = NO_MELT_AT_TEMP;
    }

    if (!list.length && near.length) {
      // Nothing covers that cold. Say so rather than returning an empty box —
      // the nearest thing is still the most useful answer we have.
      near.sort(function (a, b) { return a.gap - b.gap; });
      list = near;
      caveat = NO_MATCH_TEMP;
    } else if (!list.length && petless.length) {
      petless.sort(function (a, b) { return b.points - a.points; });
      list = petless;
      caveat = NO_MATCH_PETS;
    }

    list.sort(function (a, b) { return b.points - a.points; });

    if (this.resultList) {
      this.resultList.innerHTML = '';
      for (var n = 0; n < Math.min(list.length, 3); n++) {
        var clone = list[n].el.cloneNode(true);
        clone.removeAttribute('hidden');
        var why = document.createElement('span');
        why.className = 'quiz__why';
        why.textContent = list[n].reasons.join(' · ');
        clone.appendChild(why);
        if (list[n].caveats.length) {
          var but = document.createElement('span');
          but.className = 'quiz__caveat';
          but.textContent = list[n].caveats.join(' · ');
          clone.appendChild(but);
        }
        this.resultList.appendChild(clone);
      }
    }

    if (this.caveatMsg) {
      this.caveatMsg.textContent = caveat;
      this.caveatMsg.hidden = !caveat;
    }
    // Kept for the case where the section has no products configured at all.
    if (this.emptyMsg) this.emptyMsg.hidden = list.length > 0;
  };

  function init(root) {
    var nodes = (root || document).querySelectorAll('[data-quiz]');
    for (var i = 0; i < nodes.length; i++) new Quiz(nodes[i]);
  }

  document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
